import React, { useCallback, useEffect } from "react";
import {
  RadarChart as RechartsRadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
} from "recharts";

import styles from "./RadarChart.module.scss";
import { useInViews } from "utility/useInViews";
import { trackEvent } from "utility/mixpanel";

// Have to manually set these to overlay over the chart
const CENTER_X = 178;
const CENTER_Y = 181;
const MAX_RADIUS = 94;
const CHART_LABELS_OFFSET = 36;

// Custom tick renderer for domain label and score
const CustomAngleAxisTick = (props: any) => {
  const { payload, x, y, chartData } = props;
  // Find the score for this domain
  const score = chartData?.find((d: any) => d.domain === payload.value)?.score;
  // Move label OFFSETpx further from center in direction of label
  const centerX = typeof props.cx === "number" ? props.cx : 0;
  const centerY = typeof props.cy === "number" ? props.cy : 0;
  const dx = x - centerX;
  const dy = y - centerY;
  const length = Math.sqrt(dx * dx + dy * dy);
  const move = CHART_LABELS_OFFSET;
  const newX = centerX + (dx / length) * (length + move);
  const newY = centerY + (dy / length) * (length + move);

  const words = (payload.value as string).split(" ");
  const isWrapped = words.length > 1;
  const lineHeight = 14;
  const labelBlockHeight = (words.length - 1) * lineHeight;

  const labelDy = isWrapped ? -8 - labelBlockHeight / 2 : -4;
  const scoreDy = isWrapped ? 24 + labelBlockHeight / 2 : 18;

  return (
    <g transform={`translate(${newX},${newY})`}>
      <text className={styles["radar-label"]} dy={labelDy}>
        {words.map((word: string, i: number) => (
          <tspan key={i} x={0} dy={i === 0 ? 0 : lineHeight}>
            {word}
          </tspan>
        ))}
      </text>
      <text className={styles["radar-score"]} dy={scoreDy}>
        {score !== undefined ? score : ""}
      </text>
    </g>
  );
};

// Custom shape component for smooth polygon with rounded corners
const SmoothRadarPolygon = (props: any) => {
  const { points, ...otherProps } = props;

  if (!points || points.length === 0) {
    return null;
  }

  const createSmoothPath = (pts: any[]) => {
    if (pts.length < 3) return "";

    const tension = 0.15;
    let path = `M ${pts[0].x},${pts[0].y}`;

    for (let i = 0; i < pts.length; i++) {
      const p0 = pts[(i - 1 + pts.length) % pts.length];
      const p1 = pts[i];
      const p2 = pts[(i + 1) % pts.length];
      const p3 = pts[(i + 2) % pts.length];

      const cp1x = p1.x + (p2.x - p0.x) * tension;
      const cp1y = p1.y + (p2.y - p0.y) * tension;
      const cp2x = p2.x - (p3.x - p1.x) * tension;
      const cp2y = p2.y - (p3.y - p1.y) * tension;

      path += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`;
    }

    path += " Z";
    return path;
  };

  // Only pass valid SVG attributes to <path> to resolve issues with rendering incorrect props on DOM
  const svgProps: any = {};
  Object.keys(otherProps).forEach((key) => {
    if (
      [
        "stroke",
        "strokeWidth",
        "fill",
        "fillOpacity",
        "opacity",
        "className",
        "style",
        "id",
        "filter",
      ].includes(key)
    ) {
      svgProps[key] = otherProps[key];
    }
  });

  const pathData = createSmoothPath(points);

  return <path {...svgProps} d={pathData} />;
};

const OuterGridDots = ({
  dataPoints,
  dotsDimensions = {
    centerX: CENTER_X,
    centerY: CENTER_Y,
    maxRadius: MAX_RADIUS,
  },
}: {
  dataPoints: number;
  dotsDimensions?: { centerX: number; centerY: number; maxRadius: number };
}) => {
  const dots = [];

  for (let angle = 0; angle < dataPoints; angle++) {
    const angleInRadians = (angle * 2 * Math.PI) / dataPoints - Math.PI / 2;
    const x =
      dotsDimensions.centerX +
      dotsDimensions.maxRadius * Math.cos(angleInRadians);
    const y =
      dotsDimensions.centerY +
      dotsDimensions.maxRadius * Math.sin(angleInRadians);

    dots.push(
      <circle key={`outer-dot-${angle}`} cx={x} cy={y} r={2} fill="white" />
    );
  }

  return <g>{dots}</g>;
};

interface ChartDataPointType {
  domain: string;
  score: number;
  fullMark: number;
}

interface RadarChartPropsType {
  chartData: ChartDataPointType[];
  dotsDimensions?: { centerX: number; centerY: number; maxRadius: number };
  /**
   * The report's designs draw the chart as a straight-edged polygon on a
   * matching polygonal web with a dot at each vertex, where the Index tab
   * smooths it over concentric circles. Defaulted to the Index tab's
   * treatment so this stays a report-only opt-in.
   */
  outline?: "smooth" | "polygon";
}

const RadarChart = ({
  chartData,
  dotsDimensions,
  outline = "smooth",
}: RadarChartPropsType) => {
  const { ref, inView } = useInViews();

  const logImpression = useCallback(() => {
    trackEvent("Radar Chart Impression", {
      chartDomains: chartData.map((d) => d.domain),
      chartScores: chartData.map((d) => d.score),
      chartCount: chartData.length,
      currentPage: window.location.href,
    });
  }, [chartData]);
  useEffect(() => {
    if (inView) {
      logImpression();
    }
  }, [inView, logImpression]);

  if (!chartData || chartData.length === 0) {
    return null;
  }

  // Sizes the overlay viewBox so the dots scale with the chart, not fixed px.
  const overlayDims = dotsDimensions ?? {
    centerX: CENTER_X,
    centerY: CENTER_Y,
    maxRadius: MAX_RADIUS,
  };

  return (
    <div className={styles["chart-container"]} ref={ref}>
      <ResponsiveContainer
        width="100%"
        height="100%"
        initialDimension={{ width: 320, height: 200 }}
      >
        <RechartsRadarChart
          data={chartData}
          margin={{ top: 40, right: 60, bottom: 40, left: 60 }}
          cx="50%"
          cy="50%"
        >
          <PolarGrid
            gridType={outline === "polygon" ? "polygon" : "circle"}
            radialLines={true}
            stroke="rgba(255, 255, 255, 0.15)"
            strokeWidth={1}
          />
          <PolarAngleAxis
            dataKey="domain"
            tick={(props) => (
              <CustomAngleAxisTick {...props} chartData={chartData} />
            )}
            axisLine={false}
          />
          <PolarRadiusAxis
            angle={0}
            domain={[0, 100]}
            tick={false}
            // A polygonal web drops the zero-radius ring, so five ticks
            // draw the four the report's designs show. The Index tab's six
            // circles are left alone. The designs' innermost ring sits a
            // little tighter than evenly-spaced ticks can place it, which is
            // the one part of the web this does not reproduce exactly.
            tickCount={outline === "polygon" ? 5 : 6}
            axisLine={false}
          />
          <Radar
            dataKey="score"
            stroke="rgba(255, 255, 255, 0.9)"
            fill="rgba(255, 255, 255, 0.3)"
            fillOpacity={0.8}
            strokeWidth={2}
            dot={
              outline === "polygon"
                ? { r: 3, fill: "#f4efea", stroke: "none" }
                : false
            }
            shape={outline === "polygon" ? undefined : <SmoothRadarPolygon />}
            isAnimationActive={false}
          />
        </RechartsRadarChart>
      </ResponsiveContainer>

      <svg
        className={styles["chart-overlay-svg"]}
        viewBox={`0 0 ${overlayDims.centerX * 2} ${overlayDims.centerY * 2}`}
        preserveAspectRatio="xMidYMid meet"
      >
        <OuterGridDots
          dataPoints={chartData.length}
          dotsDimensions={overlayDims}
        />
      </svg>
    </div>
  );
};

export default RadarChart;
